// src/utils/useMempoolWebSocket.js

import { useState, useEffect, useRef, useMemo } from "react";
import { throttle } from './throttle';
export const useMempoolWebSocket = () => {
  const [message, setMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const webSocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const healthCheckIntervalRef = useRef(null);
  const lastDataTimeRef = useRef(Date.now());
  const url = "wss://mempool.space/api/v1/ws";
  const memoizedUrl = useMemo(() => url, []);
  const onOpenActions = useMemo(() => [{ action: "want", data: ["blocks", "stats", "mempool-blocks"] }, { action: "init" }], []);
  
  // Selective throttling: blocks should update immediately, stats can be throttled
  const throttledSetMessage = useMemo(
    () => throttle((msg) => {
      setMessage(msg);
    }, 2000),
    []
  );
  useEffect(() => {
    let keepAliveInterval;
    const connectWebSocket = () => {
      webSocketRef.current = new WebSocket(memoizedUrl);
      webSocketRef.current.onopen = () => {
        // console.log("WebSocket connected:");
        setIsConnected(true);
        lastDataTimeRef.current = Date.now();
        
        if (webSocketRef.current.readyState === WebSocket.OPEN) {
          onOpenActions.forEach((action) => {
            webSocketRef.current.send(JSON.stringify(action));
          });

          // Keep-alive interval - mempool.space expects ping/pong
          keepAliveInterval = setInterval(() => {
            if (webSocketRef.current.readyState === WebSocket.OPEN) {
              webSocketRef.current.send('ping');
            }
          }, 9000); // 9 seconds - original timing
        }
      };

      webSocketRef.current.onmessage = (event) => {
        // Update last data time for health monitoring
        lastDataTimeRef.current = Date.now();
        
        try {
          const data = JSON.parse(event.data);
          // Don't throttle block updates - they're important events
          if (data.block || data.blocks) {
            setMessage(event.data);
          } else {
            // Throttle other updates like stats, mempool-blocks, etc.
            throttledSetMessage(event.data);
          }
        } catch (error) {
          // If parsing fails, just pass through
          setMessage(event.data);
        }
      };

      webSocketRef.current.onerror = (event) => {
        // console.error("WebSocket error:", event);
      };

      webSocketRef.current.onclose = (event) => {
        // console.log("WebSocket connection closed:", event);
        // console.log("Reason:", event.reason);
        setIsConnected(false);
        clearInterval(keepAliveInterval);
        
        // Don't attempt to reconnect if we're shutting down
        if (event.code === 1000 || event.wasClean) {
          return;
        }
        
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          // console.log("Attempting to reconnect...");
          connectWebSocket();
        }, 5000); // 5 seconds - faster reconnect
      };
    };
    
    // Handle visibility change events (when PWA returns from background)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        // Check if WebSocket is closed or closing and reconnect immediately
        if (!webSocketRef.current || 
            webSocketRef.current.readyState === WebSocket.CLOSED ||
            webSocketRef.current.readyState === WebSocket.CLOSING) {
          connectWebSocket();
        }
      }
    };
    
    // Handle focus events as a fallback
    const handleFocus = () => {
      handleVisibilityChange();
    };
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    connectWebSocket();
    
    // Health check interval - monitor connection and data flow
    healthCheckIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const timeSinceLastData = now - lastDataTimeRef.current;
      
      // If no data for 30 seconds, check connection state
      if (timeSinceLastData > 30000) {
        // console.log('No mempool data for 30 seconds, checking connection...');
        
        // Check if WebSocket is actually connected
        if (!webSocketRef.current || 
            webSocketRef.current.readyState !== WebSocket.OPEN) {
          // console.log('WebSocket not connected, attempting reconnect...');
          
          // Clear any pending reconnect
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
          
          // Close existing connection if any
          if (webSocketRef.current) {
            webSocketRef.current.close();
          }
          
          // Reconnect
          connectWebSocket();
        } else {
          // Connection is open but no data, send a ping
          try {
            webSocketRef.current.send('ping');
          } catch (e) {
            // If ping fails, reconnect
            connectWebSocket();
          }
        }
      }
    }, 10000); // Check every 10 seconds
    
    return () => {
      // Remove event listeners
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      // Clear health check interval
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
      
      if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.close(1000, 'Component unmount');
      }
      clearInterval(keepAliveInterval);
      // Cancel any pending throttled function calls
      throttledSetMessage.cancel();
    };
  }, [memoizedUrl, onOpenActions, throttledSetMessage]);
  
  const reconnect = () => {
    // If already connected, do nothing
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      return;
    }
    
    // If already connecting, do nothing
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.CONNECTING) {
      return;
    }
    
    // Close any existing connection
    if (webSocketRef.current) {
      webSocketRef.current.close();
    }
  };
  
  return { message, reconnect, isConnected };
};
